import { makeStyles } from "@material-ui/core";
import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Select } from "antd";
import { REPORT_FONT, useReportFonts } from "../reportFonts";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";
import { useHistory, useParams } from "react-router-dom";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
  HorizontalDateRangeFilter,
  FilterApplyButton,
} from "./GlobalFilterPanel";
import { useExpandedAppliedFilters } from "./useExpandedAppliedFilters";
import {
  applyCascadeDraftChange,
  pruneInvalidCascadeSelections,
  getFilterFieldOptions,
  toFilterSelectOptions,
  FILTER_SELECT_SEARCH_PROPS,
  GlobalMultiSelectFilter,
} from "./filterRelationshipUtils";
import SummaryTabComponent from "./labor-utilization/Summary";
import DetailsTabComponent from "./labor-utilization/Details";
import FutureEmplNotesTabComponent from "./labor-utilization/FutureEmplNotes";
import BUUtilization from "./labor-utilization/BUUtilization";
import {
  usePlByProgramStyles,
  PlPageHeader,
} from "./pl-by-program/plByProgramTheme";
import Loading from "../../../common-components/Loading";
import {
  downloadLaborUtilizationSummaryAPI,
  downloadLaborUtilizationDetailsAPI,
  downloadLaborUtilizationFutureEmplNotesAPI,
  downloadLaborUtilizationBuUtilizationAPI,
} from "../../../api/reports-api";
import {
  TH_BG,
  TH_BG_BAND,
  TH_ACTUAL_BG,
  TH_ACTUAL_TEXT,
} from "../reportTableConfig";


const PAGE_BG = "#F4F5F7";
const HEADER_BG = TH_BG;
const HEADER_TEXT = "rgba(255, 255, 255, 0.9)";
const HEADER_ROW = TH_BG;
const HAIRLINE   = "#E2E8F0";
const TEXT_PRIMARY = "#111827";
const TEXT_MUTED   = "rgb(51, 65, 85)";
const TAB_ACTIVE = "#4F46E5";
const BORDER     = "#E5E7EB";
const ACTUAL_TH_BG = TH_ACTUAL_BG;
const ACTUAL_TH_FG = TH_ACTUAL_TEXT;

/* ══════════════════════════════════════════════════════════════ */
/*  Tab definitions                                               */
/* ══════════════════════════════════════════════════════════════ */
const TABS = [
  { key: "summary",           label: "Summary"          },
  { key: "details",           label: "Details"          },
  { key: "future_empl_notes", label: "Future Empl Notes" },
  { key: "bu_utilization",    label: "BU Utilization"   },
];

/** Global filters that the Labor Utilization API actually applies (LABOR_UTILIZATION_API.md). */
const LU_FILTERS = [
  "year",
  "contractId",
  "projectId",
  "orgId",
  "bu",
  "contractType",
  "clinLvl2",
  "competitionType",
  "awardType",
  "sector",
  "popEndDate",
  "employeeId",
  "employeeType",
];

const DOWNLOAD_FN = {
  summary:           downloadLaborUtilizationSummaryAPI,
  details:           downloadLaborUtilizationDetailsAPI,
  future_empl_notes: downloadLaborUtilizationFutureEmplNotesAPI,
  bu_utilization:    downloadLaborUtilizationBuUtilizationAPI,
};

function isOnlyDefaultYear(filterValues, vKeys) {
  const keys = vKeys
    ? vKeys.filter((k) => k !== "year")
    : FILTERS_CONFIG.filter((f) => f.key !== "year").map((f) => f.key);
  return (
    !keys.some((k) => {
      const f = FILTERS_CONFIG.find((fc) => fc.key === k);
      return f && isFilterActive(f, filterValues[k]);
    }) && String(filterValues.year) === String(new Date().getFullYear())
  );
}

/* ── Horizontal filter bar (matches PL-by-Program's filter popup) ── */
function HorizontalFilterBar({ visibleKeys, draft, dropdownOptions, onChange, onClearAll, onApply, onClose, classes, clearAllDisabled, applyDisabled }) {
  const filters = visibleKeys
    ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
    : FILTERS_CONFIG;

  const selectStyle = { minWidth: 110, maxWidth: 180, fontSize: 12 };

  return (
    <div className={classes.hFilterBar}>
      {filters.map((f) => {
        const opts = toFilterSelectOptions(f.key, getFilterFieldOptions(f, dropdownOptions));
        const val  = draft[f.key];

        return (
          <div key={f.key} className={classes.hFilterGroup}>
            <span className={classes.hFilterLabel}>{f.label}</span>

            {f.type === "select" && (
              <Select
                size="small"
                style={selectStyle}
                value={val || (f.key === "year" ? opts[0]?.value : "All")}
                onChange={(v) => onChange(f.key, v === "All" ? f.defaultValue : v)}
                options={f.key === "year" ? opts : [{ value: "All", label: "All" }, ...opts]}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}

            {f.type === "multiselect" && (
              <GlobalMultiSelectFilter
                size="small"
                style={selectStyle}
                value={val}
                options={opts}
                onChange={(v) => onChange(f.key, v)}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}

            {f.type === "text" && (
              <input
                className={classes.hFilterInput}
                value={val || ""}
                placeholder="All"
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
            {f.type === "daterange" && (
              <HorizontalDateRangeFilter
                value={val}
                onChange={(next) => onChange(f.key, next)}
              />
            )}
          </div>
        );
      })}

      <div className={classes.hFilterActions}>
        <button className={classes.hFilterClearAll} onClick={onClearAll} disabled={clearAllDisabled}>Clear All</button>
        <FilterApplyButton
          draft={draft}
          onApply={onApply}
          className={classes.hFilterApply}
          disabled={applyDisabled}
        >
          Apply
        </FilterApplyButton>
      </div>

      <button className={classes.hFilterClose} onClick={onClose} title="Close Filters">✕</button>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════ */
/*  Column & data definitions                                     */
/* ══════════════════════════════════════════════════════════════ */
const MONTHS = [
  { key: "jan", label: "Jan" },
  { key: "feb", label: "Feb" },
  { key: "mar", label: "Mar" },
  { key: "apr", label: "Apr" },
  { key: "may", label: "May" },
  { key: "jun", label: "Jun" },
  { key: "jul", label: "Jul" },
  { key: "aug", label: "Aug" },
  { key: "sep", label: "Sep" },
  { key: "oct", label: "Oct" },
  { key: "nov", label: "Nov" },
  { key: "dec", label: "Dec" },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Helpers                                                       */
/* ══════════════════════════════════════════════════════════════ */
const NEG_COLOR = "#DC2626";

const fmtHours = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  const abs = Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `(${abs})` : abs;
};

const isNeg = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n < 0;
};

const negStyle = (v) => (isNeg(v) ? { color: NEG_COLOR } : undefined);

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  "@keyframes fadeUp": {
    from: { opacity: 0 },
    to:   { opacity: 1 },
  },

  page: {
    fontFamily: REPORT_FONT,
    backgroundColor: PAGE_BG,
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },

  /* ── nav bar ── */
  topBar: {
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "rgb(51, 65, 85)" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },

  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: TAB_ACTIVE,
    background: TAB_ACTIVE,
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  downloadBtn: {
    borderColor: "#4F46E5",
    background: "#4F46E5",
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  /* ── tab bar (matches Waterfall / EAC) ── */
  tabBar: {
    background: "#fff",
    borderBottom: `1px solid ${BORDER}`,
    display: "flex",
    flexWrap: "wrap",
    flexShrink: 0,
    padding: "0 24px",
    gap: "4px 0",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "none",
    marginBottom: -1,
    transition: "color 0.15s, border-color 0.15s",
    fontFamily: REPORT_FONT,
    "&:hover": { color: TAB_ACTIVE },
  },
  tabActive: {
    color: TAB_ACTIVE,
    fontWeight: 700,
    borderBottom: `2px solid ${TAB_ACTIVE}`,
  },

  /* ── horizontal filter bar (matches P&L by Program) ── */
  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: `1px solid ${BORDER}`,
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: REPORT_FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: `1px solid ${TAB_ACTIVE}`, borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: TAB_ACTIVE,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
  "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: REPORT_FONT,
    "&:hover": { color: TAB_ACTIVE, borderColor: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: REPORT_FONT,
    "&:hover": { background: "#111827" },
  },

  /* ── active filter chips ── */
  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: `1px solid ${BORDER}`,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel:    { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear:    { display: "flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: "50%", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 10, padding: 0, marginLeft: 2, "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" }, "&:disabled": { opacity: 0.38, cursor: "not-allowed" } },
  chipsClearAll:{ fontSize: 11, fontWeight: 600, color: "rgb(51, 65, 85)", background: "none", border: "none", cursor: "pointer", padding: "0 6px", marginLeft: 4, whiteSpace: "nowrap", flexShrink: 0, "&:hover": { color: TAB_ACTIVE }, "&:disabled": { opacity: 0.38, cursor: "not-allowed" } },

  /* ── content ── */
  content: {
    flex: 1,
    padding: "20px 16px",
    overflowY: "auto",
    minWidth: 0,
  },

  /* ── table section card (matches P&L tableSection exactly) ── */
  tableCard: {
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    overflow: "hidden",
    marginBottom: 24,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    animation: "$fadeUp 0.5s ease both",
  },
  tableCardScrollable: {
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    marginBottom: 24,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    animation: "$fadeUp 0.5s ease both",
  },
  tableSectionHead: {
    padding: "20px 24px 16px",
    borderBottom: `1px solid ${HAIRLINE}`,
    background: "#FFFFFF",
  },
  tableSectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: TEXT_PRIMARY,
    letterSpacing: "-0.02em",
    lineHeight: 1.35,
    fontFamily: REPORT_FONT,
    margin: 0,
  },
  tableSectionMeta: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 400,
    color: TEXT_MUTED,
    fontFamily: REPORT_FONT,
    lineHeight: 1.4,
  },
  tableWrap: { overflowX: "auto", overflowY: "hidden", background: "#FFFFFF" },
  tableWrapScrollable: {
    overflowX: "auto",
    overflowY: "hidden",
  },
  tableCardFooter: {
    flexShrink: 0,
  },

  /* ── table base ── */
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: REPORT_FONT,
    fontSize: 13,
    tableLayout: "auto",
  },

  /* ── band header row (row 1) ── */
  thEmpId: {
    background: HEADER_BG,
    color: HEADER_TEXT,
    padding: "12px 14px",
    fontWeight: 700,
    fontSize: 10,
    textAlign: "left",
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",

    minWidth: "80px",
    position: "sticky",
    left: 0,
    zIndex: 3,
  },
  thEmpName: {
    background: HEADER_BG,
    color: HEADER_TEXT,
    padding: "12px 14px",
    fontWeight: 700,
    fontSize: 10,
    textAlign: "left",
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    minWidth: "200px",
    position: "sticky",
    left: 80,
    zIndex: 3,
  },
  thYear: {
    background: HEADER_BG,
    color: HEADER_TEXT,
    padding: "12px 10px",
    fontWeight: 600,
    fontSize: 10,
    textAlign: "center",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",

    minWidth: "80px",
  },
  thYearBand: {
    background: HEADER_BG,
    color: HEADER_TEXT,
    padding: "12px 10px",
    fontWeight: 600,
    fontSize: 10,
    textAlign: "center",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",

  },
  /* ── column header row (row 2) ── */
  thEmpStatus: {
    background: HEADER_BG,
    color: HEADER_TEXT,
    padding: "11px 10px",
    fontWeight: 700,
    fontSize: 10,
    textAlign: "center",
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    minWidth: "80px",
    position: "sticky",
    left: 280,
    zIndex: 3,
  },
  thMonth: {
    background: HEADER_BG,
    color: HEADER_TEXT,
    padding: "11px 8px",
    fontWeight: 500,
    fontSize: 12,
    textAlign: "center",
    whiteSpace: "nowrap",

    minWidth: "80px",
  },
  thMonthActual: {
    background: ACTUAL_TH_BG,
    color: ACTUAL_TH_FG,
    padding: "11px 8px",
    fontWeight: 700,
    fontSize: 12,
    textAlign: "center",
    whiteSpace: "nowrap",
    minWidth: "80px",
  },
  tdMonthActualLabel: {
    background: ACTUAL_TH_BG,
    color: ACTUAL_TH_FG,
    fontWeight: 700,
  },
  thTotal: {
    background: HEADER_BG,
    color: "#F59E0B",
    padding: "11px 10px",
    fontWeight: 700,
    fontSize: 10,
    textAlign: "center",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",

    minWidth: "100px",
    verticalAlign: "middle",
  },

  /* ── total row ── */
  tdTotalLabel: {
    padding: "12px 14px",
    fontWeight: 700,
    color: TEXT_PRIMARY,
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${HAIRLINE}`,

    fontSize: 13,
    background: "#F0FFF4",
    position: "sticky",
    left: 0,
    zIndex: 1,
  },
  tdTotalEmpty: {
    background: "#F0FFF4",
    borderBottom: `1px solid ${HAIRLINE}`,

  },
  tdTotalMonth: {
    padding: "12px 10px",
    textAlign: "right",
    fontWeight: 600,
    color: TEXT_PRIMARY,
    borderBottom: `1px solid ${HAIRLINE}`,

    fontSize: 13,
    background: "#F0FFF4",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  tdTotalColMain: {
    padding: "12px 14px",
    textAlign: "right",
    fontWeight: 700,
    color: TEXT_PRIMARY,
    borderBottom: `1px solid ${HAIRLINE}`,

    fontSize: 13,
    background: "#E6FFF1",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },

  /* ── data row cells ── */
  tdEmpId: {
    padding: "12px 14px",
    fontWeight: 500,
    color: TEXT_PRIMARY,
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${HAIRLINE}`,

    fontSize: 13,
    background: "#FFFFFF",
    position: "sticky",
    left: 0,
    zIndex: 1,
    boxShadow: "6px 0 12px -8px rgba(0,0,0,0.06)",
  },
  tdEmpName: {
    padding: "12px 14px",
    fontWeight: 400,
    color: TEXT_PRIMARY,
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13,
    background: "#FFFFFF",
    position: "sticky",
    left: 80,
    zIndex: 1,
  },
  tdEmpStatus: {
    padding: "12px 10px",
    textAlign: "center",
    color: TEXT_MUTED,
    borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 13,
    whiteSpace: "nowrap",
    background: "#FFFFFF",
    position: "sticky",
    left: 280,
    zIndex: 1,
  },
  tdMonth: {
    padding: "12px 10px",
    textAlign: "right",
    color: TEXT_MUTED,
    borderBottom: `1px solid ${HAIRLINE}`,

    fontSize: 13,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  tdEmpTotal: {
    padding: "12px 14px",
    textAlign: "right",
    fontWeight: 700,
    color: "#F59E0B",
    borderBottom: `1px solid ${HAIRLINE}`,

    fontSize: 13,
    background: "#F8FAFC",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },

  /* ── BU Utilization tab (passed to BUUtilization via classes) ── */
  buFilterRow: {
    display: "flex",
    alignItems: "center",
    padding: "14px 24px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    background: "#FFFFFF",
    gap: 10,
  },
  buFilterGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 36,
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    flexShrink: 0,
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": {
      borderColor: "#C7D2FE",
      boxShadow: "0 3px 10px rgba(74,85,162,0.12)",
    },
    "&:focus-within": {
      borderColor: "#A5B4FC",
      boxShadow: "0 0 0 3px rgba(240,242,255,0.9)",
    },
  },
  buFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4A55A2",
    padding: "0 14px",
    background: "#F0F2FF",
    height: "100%",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    userSelect: "none",
    fontFamily: REPORT_FONT,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  buFilterSelect: {
    border: "none",
    outline: "none",
    backgroundColor: "#FFFFFF",
    fontSize: 13,
    fontWeight: 400,
    color: "#111111",
    padding: "0 28px 0 14px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: 160,
    height: "100%",
    fontFamily: REPORT_FONT,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748B' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
  },
  buChartCard: {
    background: "#fff",
    borderTop: "1px solid rgba(0,0,0,0.06)",
    paddingBottom: "8px",
  },
  buChartTitle: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 40px 10px 14px",
    fontSize: 10,
    fontWeight: 600,
    color: "#FFFFFF",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontFamily: REPORT_FONT,
    background: HEADER_BG,
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    flexShrink: 0,
  },
  maximizeBtn: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    border: "none",
    background: "transparent",
    padding: 0,
    lineHeight: 0,
    cursor: "pointer",
    color: "#FFFFFF",
    transition: "color 0.15s",
    "&:hover": { color: "#F59E0B" },
    "&:focus-visible": {
      outline: "2px solid #F59E0B",
      outlineOffset: 2,
      borderRadius: 4,
    },
  },
  buTableCard: {
    overflow: "hidden",
    border: "1px solid rgba(0,0,0,0.06)",
    marginBottom: 10,
  },
  thBu: {
    background: HEADER_BG,
    color: HEADER_TEXT,
    padding: "12px 14px",
    fontWeight: 700,
    fontSize: 10,
    textAlign: "left",
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",

  },
  thBuNum: {
    background: HEADER_BG,
    color: HEADER_TEXT,
    padding: "12px 14px",
    fontWeight: 700,
    fontSize: 10,
    textAlign: "right",
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",

  },
  tdBuMonth: {
    padding: "12px 14px",
    fontSize: 13,
    color: TEXT_PRIMARY,
    borderBottom: `1px solid ${HAIRLINE}`,

    whiteSpace: "nowrap",
    fontWeight: 500,
  },
  tdBuNum: {
    padding: "12px 14px",
    fontSize: 13,
    color: TEXT_MUTED,
    textAlign: "right",
    borderBottom: `1px solid ${HAIRLINE}`,

    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  tdBuInfinity: {
    padding: "12px 14px",
    fontSize: 13,
    color: "#94A3B8",
    textAlign: "right",
    borderBottom: `1px solid ${HAIRLINE}`,
    fontStyle: "italic",
  },
  tdBuTotalLabel: {
    padding: "12px 14px",
    fontSize: 13,
    fontWeight: 700,
    color: TEXT_PRIMARY,
    borderTop: `2px solid ${HAIRLINE}`,

    background: "#F0FFF4",
  },
  tdBuTotalNum: {
    padding: "12px 14px",
    fontSize: 13,
    fontWeight: 700,
    color: TEXT_PRIMARY,
    textAlign: "right",
    borderTop: `2px solid ${HAIRLINE}`,

    background: "#F0FFF4",
    fontVariantNumeric: "tabular-nums",
  },
  tdBuTotalInfinity: {
    padding: "12px 14px",
    fontSize: 13,
    fontWeight: 700,
    color: TEXT_PRIMARY,
    textAlign: "right",
    borderTop: `2px solid ${HAIRLINE}`,
    background: "#F0FFF4",
  },
  buFootnote: {
    fontSize: 12,
    color: TEXT_MUTED,
    padding: "12px 20px",
    lineHeight: 1.6,
    background: "#F9FAFB",
    borderTop: `1px solid ${HAIRLINE}`,
    fontFamily: REPORT_FONT,
  },

  /* ── future empl notes table ── */
  thNotes: {
    background: HEADER_BG,
    color: HEADER_TEXT,
    padding: "11px 14px",
    fontWeight: 700,
    fontSize: 10,
    textAlign: "left",
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    minWidth: "340px",
  },
  tdNotes: {
    padding: "12px 14px",
    fontWeight: 400,
    color: TEXT_PRIMARY,
    borderBottom: `1px solid ${HAIRLINE}`,

    fontSize: 13,
    whiteSpace: "normal",
    minWidth: "340px",
    lineHeight: 1.4,
  },

  /* ── details table ── */
  thDetail: {
    background: HEADER_BG,
    color: HEADER_TEXT,
    padding: "12px 14px",
    fontWeight: 700,
    fontSize: 10,
    textAlign: "left",
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",

    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  thDetailNum: {
    background: HEADER_BG,
    color: HEADER_TEXT,
    padding: "12px 14px",
    fontWeight: 700,
    fontSize: 10,
    textAlign: "right",
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",

    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  tdDetailText: {
    padding: "12px 14px",
    fontSize: 13,
    color: TEXT_MUTED,
    borderBottom: `1px solid ${HAIRLINE}`,

    whiteSpace: "nowrap",
  },
  tdDetailWrap: {
    padding: "12px 14px",
    fontSize: 13,
    color: TEXT_MUTED,
    borderBottom: `1px solid ${HAIRLINE}`,

    whiteSpace: "normal",
    minWidth: "120px",
  },
  tdDetailNum: {
    padding: "12px 14px",
    fontSize: 13,
    color: TEXT_MUTED,
    textAlign: "right",
    borderBottom: `1px solid ${HAIRLINE}`,

    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },

  /* ── placeholder ── */
  placeholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "64px 24px",
    color: "#94A3B8",
    gap: "12px",
  },
  placeholderTitle: { fontSize: "16px", fontWeight: 600, color: "#64748B" },
  placeholderSub: { fontSize: "13px" },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  Icons                                                         */
/* ══════════════════════════════════════════════════════════════ */
function FilterIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Main export                                                   */
/* ══════════════════════════════════════════════════════════════ */
export default function LaborUtilization() {
  const classes = useStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();
  const { tab: tabParam } = useParams();
  const activeTab = TABS.find((t) => t.key === tabParam)?.key ?? TABS[0].key;
  const [filterOpen, setFilterOpen] = useState(true);

  /* Per-tab filter state — each tab keeps its own independent draft/applied values */
  const [tabApplied, setTabApplied] = useState({});
  const [tabDraft,   setTabDraft]   = useState({});
  const defaultFilters = useMemo(() => initFilterValues(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const appliedFilters = tabApplied[activeTab] ?? defaultFilters;
  const draftFilters   = tabDraft[activeTab]   ?? defaultFilters;

  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  const setAppliedFilters = useCallback((updater) => {
    const tab = activeTabRef.current;
    setTabApplied((prev) => ({
      ...prev,
      [tab]: typeof updater === "function" ? updater(prev[tab] ?? initFilterValues()) : updater,
    }));
  }, []);

  const setDraftFilters = useCallback((updater) => {
    const tab = activeTabRef.current;
    setTabDraft((prev) => ({
      ...prev,
      [tab]: typeof updater === "function" ? updater(prev[tab] ?? initFilterValues()) : updater,
    }));
  }, []);

  const [summaryMeta, setSummaryMeta] = useState(null);
  const [detailsMeta, setDetailsMeta] = useState(null);
  const [buMeta, setBuMeta] = useState(null);
  const [futureEmplMeta, setFutureEmplMeta] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const visibleKeys = LU_FILTERS;
  const { dropdownOptions, relationshipRows, effectiveFilters } = useExpandedAppliedFilters(appliedFilters, draftFilters, visibleKeys);

  useEffect(() => {
    if (filterOpen) {
      setDraftFilters(pruneInvalidCascadeSelections(appliedFilters, relationshipRows, visibleKeys));
    }
  }, [filterOpen, appliedFilters, relationshipRows, visibleKeys]);

  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = (visibleKeys ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key)) : FILTERS_CONFIG)
    .filter((f) => isFilterActive(f, appliedFilters[f.key]));

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    const reset = (prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    };
    setAppliedFilters(reset);
    setDraftFilters(reset);
  }, [visibleKeys]);

  const handleDraftChange = useCallback((key, value) => {
    setDraftFilters((prev) =>
      applyCascadeDraftChange(prev, key, value, relationshipRows, visibleKeys),
    );
  }, [relationshipRows, visibleKeys]);

  const handleApply = useCallback(() => {
    setAppliedFilters(pruneInvalidCascadeSelections(draftFilters, relationshipRows, visibleKeys));
    setFilterOpen(false);
  }, [draftFilters, relationshipRows, visibleKeys]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  const handleDownload = useCallback(async () => {
    const downloadFn = DOWNLOAD_FN[activeTab];
    if (!downloadFn || downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await downloadFn(effectiveFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Labor Utilization download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [activeTab, effectiveFilters, downloading]);

  const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const filterYear =
    appliedFilters?.year && appliedFilters.year !== "All"
      ? appliedFilters.year
      : new Date().getFullYear();

  // Actuals-through is live cutoff — do not follow the Year filter
  const actualsThroughLabel = (meta) => {
    const m = Number(meta?.actualThroughMonth);
    const year = new Date().getFullYear();
    if (!m || m < 1 || m > 12) return `Actuals through May ${year}`;
    return `Actuals through ${MONTH_NAMES[m - 1]} ${year}`;
  };

  const fyLabel = `FY-${filterYear}`;

  const TAB_META = {
    summary:           `Employee Hours by Month · ${fyLabel}`,
    details:           `Project-level Labor Allocations · ${fyLabel}`,
    future_empl_notes: `Planned Headcount & Hour Forecasts · ${fyLabel}`,
    bu_utilization:    `Utilization % by Business Unit · ${fyLabel}`,
  };

  const TAB_HEADER = {
    summary:           { eyebrow: "Labor Utilization", title: "Summary",           accent: "Hours by Employee",       badge: "Live Forecast", date: actualsThroughLabel(summaryMeta) },
    details:           { eyebrow: "Labor Utilization", title: "Details",            accent: "Project Allocations",     badge: "Live Forecast", date: actualsThroughLabel(detailsMeta) },
    future_empl_notes: { eyebrow: "Labor Utilization", title: "Future Empl Notes", accent: "Planned Headcount",       badge: "Live Forecast", date: actualsThroughLabel(futureEmplMeta) },
    bu_utilization:    { eyebrow: "Labor Utilization", title: "BU Utilization",    accent: "Utilization by BU",       badge: "Live Forecast", date: actualsThroughLabel(buMeta) },
  };

  const renderContent = () => {
    const hdr = TAB_HEADER[activeTab];
    const head = (
      <div className={classes.tableSectionHead}>
        <h2 className={classes.tableSectionTitle}>
          {TABS.find((t) => t.key === activeTab)?.label}
        </h2>
        <div className={classes.tableSectionMeta}>{TAB_META[activeTab]}</div>
      </div>
    );

    const pageHeader = (
      <PlPageHeader
        classes={plClasses}
        eyebrow={hdr.eyebrow}
        title={hdr.title}
        accent={hdr.accent}
        badge={hdr.badge}
        date={hdr.date}
      />
    );

    if (activeTab === "summary")
      return (
        <>
          {pageHeader}
          <div className={classes.tableCardScrollable}>
            {head}
            <SummaryTabComponent
              classes={classes}
              MONTHS={MONTHS}
              fmtHours={fmtHours}
              negStyle={negStyle}
              filters={effectiveFilters}
              onMeta={setSummaryMeta}
            />
          </div>
        </>
      );
    if (activeTab === "details")
      return (
        <>
          {pageHeader}
          <div className={classes.tableCard}>
            {head}
            <DetailsTabComponent classes={classes} filters={effectiveFilters} onMeta={setDetailsMeta} />
          </div>
        </>
      );
    if (activeTab === "future_empl_notes")
      return (
        <>
          {pageHeader}
          <div className={classes.tableCardScrollable}>
            {head}
            <FutureEmplNotesTabComponent
              classes={classes}
              MONTHS={MONTHS}
              fmtHours={fmtHours}
              negStyle={negStyle}
              filters={effectiveFilters}
              onMeta={setFutureEmplMeta}
            />
          </div>
        </>
      );
    if (activeTab === "bu_utilization")
      return (
        <>
          {pageHeader}
          <div className={classes.tableCard}>
            {head}
            <BUUtilization classes={classes} filters={effectiveFilters} onMeta={setBuMeta} />
          </div>
        </>
      );
    return null;
  };

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {/* ── Navigation bar ── */}
      <div className={classes.topBar}>
        <BackButton onClick={() => goToReportsCatalog(history)} ariaLabel="Back to Reports" />
        <nav className={classes.crumb}>
          <span
            className={classes.crumbLink}
            onClick={() => goToReportsCatalog(history)}
          >
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>Labor Utilization</span>
        </nav>
        <div className={classes.topActions}>
          <button
            type="button"
            className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.downloadBtn}`}
            title="Download as Excel"
            aria-label="Download Report"
            onClick={handleDownload}
            disabled={downloading}
          >
            <DownloadIcon />
          </button>
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <FilterIcon />
            Filters
            {filterCount > 0 && (
              <span className={classes.filterBadge}>{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() => history.push(`/projects/reports/labor_utilization/${tab.key}`)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <HorizontalFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isOnlyDefaultYear(draftFilters, visibleKeys)}
        applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, visibleKeys)}
        />
      )}

      {/* ── Active filter chips (shown when filter bar is hidden) ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip = f.type === "multiselect" && Array.isArray(val) && val.length > 1
              ? `${f.label}: ${val.join(", ")}`
              : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${f.label}`}
                  disabled={f.key === "year" && String(val) === String(new Date().getFullYear())}
                >✕</button>
              </span>
            );
          })}
          <button className={classes.chipsClearAll} onClick={clearAllApplied} disabled={isOnlyDefaultYear(appliedFilters, visibleKeys)}>Clear All</button>
        </div>
      )}

      {/* ── Content ── */}
      <div className={classes.body}>
        <div className={classes.content}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
